import { useCallback } from "react";

interface LoginResponse {
  token: string;
  user: {
    id: number;
    email: string;
    role: string;
  };
  draftPitches?: {
    pitches: any[];
    totalItems: number;
  };
}

const storageKey = "draft_pitches";

export const useJwtAuth = () => {
  const decodeToken = useCallback((token: string) => {
    try {
      const [, payloadBase64] = token.split(".");
      const normalizedBase64 = payloadBase64
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      const payload = JSON.parse(atob(normalizedBase64));
      return payload;
    } catch (error) {
      return null;
    }
  }, []);

  const validateToken = useCallback(
    (token: string | null) => {
      if (!token) return false;
      const decoded = decodeToken(token);
      if (!decoded?.exp) return false;
      return Math.floor(Date.now() / 1000) < decoded.exp;
    },
    [decodeToken]
  );

  const handleLoginSuccess = useCallback((response: LoginResponse) => {
    if (response.token) {
      localStorage.setItem("accessToken", response.token);
    }

    if (response.user) {
      localStorage.setItem("user", JSON.stringify(response.user));
    }

    if (response.draftPitches) {
      localStorage.setItem(storageKey, JSON.stringify(response.draftPitches));
    }

    return response;
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("user");
    localStorage.removeItem(storageKey);
  }, []);

  // Get stored token
  const getToken = useCallback(() => {
    return localStorage.getItem("accessToken");
  }, []);

  // Get stored user
  const getUser = useCallback(() => {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  }, []);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const token = getToken();
    return validateToken(token);
  }, [getToken, validateToken]);

  return {
    decodeToken,
    validateToken,
    handleLoginSuccess,
    handleLogout,
    getToken,
    getUser,
    isAuthenticated,
  };
};
